import { ReactNode, useMemo } from "react";

import { GenericHvacEquipment, TerminalCoolerUnits, TerminalHeaterUnits } from "gql/graphql";
import {
    AirHandlingEquipmentImage,
    DomesticHotWaterHeaterImage,
    RooftopUnitImage,
    TerminalUnitsImage,
} from "./Survey.images";

import {
    ActiveView,
    DEFAULT_AIR_HANDLING_EQUIPMENT,
    DEFAULT_CENTRAL_PLANT_COOLER,
    DEFAULT_CENTRAL_PLANT_GENERIC_EQUIPMENT,
    DEFAULT_CENTRAL_PLANT_HEATER,
    DEFAULT_CENTRAL_PLANT_HEAT_PUMP,
    DEFAULT_DOMESTIC_HOT_WATER,
    DEFAULT_HEAT_PUMP,
    DEFAULT_OTHER_EQUIPMENT,
    DEFAULT_ROOFTOP_UNIT,
    DEFAULT_TERMINAL_COOLER,
    DEFAULT_TERMINAL_HEATER,
    ENERGY_DENSITY_PLACEHOLDER,
    FURNACES,
    NONE,
    PTAC_HEATERS,
    QUANTITY_INPUT_PLACEHOLDER,
    SIZE_INPUT_PLACEHOLDER,
    YEAR_INPUT_PLACEHOLDER,
} from "./Survey.constants";

import {
    CentralPlant,
    TerminalUnits,
    equipmentKeyToLabelMap,
} from "./SurveyElements/Equipment/BuildingEquipment.constants";
import { EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY } from "./SurveyElements/Equipment/equipmentSurveyFieldUnits";

interface SurveyUIStructItem<T> {
    label: string;
    key: keyof T | null;
    type: "label" | "subtitle" | "boolean" | "select" | "checkbox" | "checkboxWithSelect" | "genericEquipment" | "text";
    description?: string;
    image?: ReactNode;
    show: boolean;
    hint?: string;
    items?: EquipmentAndInstallationYearSurveyItem<T>[];
}

export interface SurveyUIStructSelect<T> extends SurveyUIStructItem<T> {
    key: keyof T;
    options: T[keyof T][];
    type: "select";
}

export interface SurveyUIStructBoolean<T> extends SurveyUIStructItem<T> {
    key: keyof T;
    type: "boolean";
}

export interface SurveyUIStructCheckboxWithSelect<T> extends SurveyUIStructItem<T> {
    key: keyof T;
    options: {
        values: T[keyof T][];
        key: keyof T;
        selectFieldLabel?: string;
    };
    type: "checkboxWithSelect";
}

export interface SurveyUIStructCheckbox<T> extends SurveyUIStructItem<T> {
    key: keyof T;
    type: "checkbox";
}

export interface SurveyUIStructText<T> extends SurveyUIStructItem<T> {
    key: keyof T | null;
    type: "text";
    value: string;
}

export type SurveyUIStruct<T> = (
    | SurveyUIStructSelect<T>
    | SurveyUIStructCheckboxWithSelect<T>
    | SurveyUIStructBoolean<T>
    | SurveyUIStructCheckbox<T>
    | SurveyUIStructItem<T>
    | SurveyUIStructText<T>
)[];

export const buildRooftopUnitsSurveyUIStruct = (
    roofTopUnit: RooftopUnit,
    airHandlingEquipment: AirHandlingEquipment
): SurveyUIStruct<RooftopUnit> => [
    {
        label: "Does the building have packaged rooftop units?",
        key: "rooftopUnitExists",
        type: "boolean",
        image: <RooftopUnitImage />,
        description: "A self-contained HVAC system that combines heating and cooling components in a single unit.",
        show: airHandlingEquipment.airHandlingEquipmentExists !== false,
    },
    {
        label: "Does the building have packaged rooftop units?",
        key: null,
        type: "label",
        image: <RooftopUnitImage />,
        description: "A self-contained HVAC system that combines heating and cooling components in a single unit.",
        show: airHandlingEquipment.airHandlingEquipmentExists === false,
    },
    {
        label: "What is their heating type?",
        key: "rooftopUnitHeatingType",
        type: "select",
        options: ["electric_resistance", "gas", "none"],
        show: roofTopUnit.rooftopUnitExists === true,
    },
    {
        label: "What is their cooling type?",
        key: null,
        value: "direct_expansion",
        type: "text",
        show: roofTopUnit.rooftopUnitExists === true,
    },
];

export const buildAirHandlingEquipmentSurveyUIStruct = (
    airHandlingEquipment: AirHandlingEquipment,
    rooftopUnit: RooftopUnit
): SurveyUIStruct<AirHandlingEquipment> => {
    const airHandlerIsMandatory = rooftopUnit.rooftopUnitExists === false;

    const airHandlerExists = airHandlerIsMandatory || airHandlingEquipment.airHandlingEquipmentExists === true;

    return [
        {
            label: "Does the building have air handling equipment?",
            key: null,
            type: "label",
            image: <AirHandlingEquipmentImage />,
            description: "Equipment that conditions and circulates air.",
            show: airHandlerIsMandatory,
        },
        {
            label: "Does the building have air handling equipment?",
            key: "airHandlingEquipmentExists",
            type: "boolean",
            image: <AirHandlingEquipmentImage />,
            description: "Equipment that conditions and circulates air.",
            show: !airHandlerIsMandatory,
        },
        {
            label: "What kind?",
            key: "airHandlingEquipmentType",
            type: "select",
            options: [
                "make_up_air_unit",
                "packaged_air_handling_unit",
                "split_air_handling_unit",
                "suite_energy_recovery_ventilator",
                "suite_air_exchangers",
                "exhaust_only_air_handling_unit",
            ],
            show: airHandlerExists,
        },
        {
            label: "What is its heating type?",
            key: "airHandlingEquipmentHeatingType",
            type: "select",
            options: ["gas", "electric_resistance", "hydronic", "none"],
            show:
                airHandlerExists &&
                airHandlingEquipment.airHandlingEquipmentType !== "suite_energy_recovery_ventilator" &&
                airHandlingEquipment.airHandlingEquipmentType !== "suite_air_exchangers" &&
                airHandlingEquipment.airHandlingEquipmentType !== "exhaust_only_air_handling_unit",
        },
        {
            label: "What is its cooling type?",
            key: "airHandlingEquipmentCoolingType",
            type: "select",
            options: ["hydronic", "direct_expansion", "none"],
            show:
                airHandlerExists &&
                airHandlingEquipment.airHandlingEquipmentType !== "suite_energy_recovery_ventilator" &&
                airHandlingEquipment.airHandlingEquipmentType !== "suite_air_exchangers" &&
                airHandlingEquipment.airHandlingEquipmentType !== "exhaust_only_air_handling_unit",
        },
    ];
};

export const buildCentralPlantSurveyUIStruct = (
    centralPlant: CentralPlant,
    airHandlingEquipment: AirHandlingEquipment
): SurveyUIStruct<CentralPlant> => {
    const centralPlantIsMandatory =
        airHandlingEquipment.airHandlingEquipmentCoolingType === "hydronic" ||
        airHandlingEquipment.airHandlingEquipmentHeatingType === "hydronic";

    const centralPlantExists = centralPlant.centralPlantExists || centralPlantIsMandatory;

    return [
        {
            label: "Does the building have a central plant?",
            key: null,
            type: "label",
            description: "A central hydronic plant houses equipment like boilers, chillers, and heat pumps.",
            show: centralPlantIsMandatory,
        },
        {
            label: "Does the building have a central plant?",
            key: "centralPlantExists",
            type: "boolean",
            description: "A central hydronic plant houses equipment like boilers, chillers, and heat pumps.",
            show: !centralPlantIsMandatory,
        },
        {
            label: "Which type of boiler is used?",
            key: "centralPlantHeaterType",
            type: "select",
            options: buildCentralPlantBoilerOptions(),
            show: centralPlantExists === true,
        },
        {
            label: "Which type of heat pump is used?",
            key: "centralPlantHeatPumpType",
            type: "select",
            options: buildCentralPlantHeatPumpOptions(airHandlingEquipment, centralPlant),
            show: centralPlantExists === true,
        },
        {
            label: "Which type of terminal units is used?",
            key: "centralPlantHeaterTerminalUnits",
            type: "select",
            options: buildCentralPlantHeaterTerminalUnitOptions(airHandlingEquipment, centralPlant),
            show: centralPlantExists === true,
        },
        {
            label: "Which type of chiller does the it have?",
            key: "centralPlantCoolerType",
            type: "select",
            options: buildCentralPlantChillerOptions(airHandlingEquipment),
            show:
                centralPlantExists === true &&
                centralPlant.centralPlantHeaterTerminalUnits !== "water_loop_heat_pump" &&
                centralPlant.centralPlantHeaterTerminalUnits !== "split_air_source_heat_pump" &&
                centralPlant.centralPlantHeatPumpType === "none",
        },
        {
            label: "Which type of chiller does the it have?",
            key: null,
            value: "none",
            type: "text",
            show:
                centralPlant.centralPlantHeaterTerminalUnits === "water_loop_heat_pump" ||
                centralPlant.centralPlantHeaterTerminalUnits === "split_air_source_heat_pump",
        },
        {
            label: "Which type of terminal units does the it have?",
            key: "centralPlantCoolerTerminalUnits",
            type: "select",
            options: ["fan_coil_units", "baseboards", "constant_volume_boxes", "variable_air_volume_boxes", "none"],
            show:
                centralPlantExists === true &&
                centralPlant.centralPlantCoolerType !== "none" &&
                centralPlant.centralPlantHeaterTerminalUnits !== "water_loop_heat_pump" &&
                centralPlant.centralPlantHeaterTerminalUnits !== "split_air_source_heat_pump" &&
                centralPlant.centralPlantHeatPumpType === "none",
        },
        {
            label: "Does the building have custom central plant equipment?",
            key: null,
            type: "genericEquipment",
            show: centralPlantExists === true,
            items: [
                {
                    label: "How should we call it?",
                    key: "centralPlantGenericEquipmentName",
                    type: "text",
                    placeholder: "Enter name",
                    show: centralPlantExists === true,
                },
                {
                    label: "What is its total capacity?",
                    key: "centralPlantGenericEquipmentSize",
                    type: "number",
                    unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.centralPlantGenericEquipmentSize,
                    placeholder: "Enter size",
                    show: centralPlantExists === true,
                },
                {
                    label: "What are the capacity units?",
                    key: "centralPlantGenericEquipmentSizeUnits",
                    type: "select",
                    options: ["cfm", "mbtu", "tons"],
                    show: centralPlantExists === true,
                },
                {
                    label: "What ratio of the load is it carrying?",
                    key: "centralPlantGenericEquipmentLoadRatio",
                    type: "number",
                    placeholder: "Enter load ratio",
                    show: centralPlantExists === true,
                },
                {
                    label: "What is its coefficient of performance?",
                    key: "centralPlantGenericEquipmentCoefficientOfPerformance",
                    type: "number",
                    placeholder: "Enter coefficient of performance",
                    show: centralPlantExists === true,
                },
                {
                    label: "Which fuel is it using?",
                    key: "centralPlantGenericEquipmentFuel",
                    type: "select",
                    options: ["electricity", "natural_gas"],
                    show: centralPlantExists === true,
                },
                {
                    label: "What type of end use does it serve?",
                    key: "centralPlantGenericEquipmentEndUse",
                    type: "select",
                    options: ["outdoor_air_heating", "outdoor_air_cooling", "skin_heating", "skin_cooling"],
                },
                {
                    label: "When were they installed?",
                    type: "number",
                    key: "centralPlantGenericEquipmentInstallationYear",
                    show: centralPlantExists === true,
                    placeholder: YEAR_INPUT_PLACEHOLDER,
                },
            ],
        },
        {
            label: "Does the building have a central plant cooler?",
            key: "centralPlantCoolerExists",
            type: "label",
            show: false,
        },
        {
            label: "Does the building have a central plant heater?",
            key: "centralPlantHeaterExists",
            type: "label",
            show: false,
        },
        {
            label: "Does the building have a central plant heat pump?",
            key: "centralPlantHeatPumpExists",
            type: "label",
            show: false,
        },
        {
            label: "Does the building have custom central plant equipment?",
            key: "centralPlantGenericEquipmentExists",
            type: "label",
            show: false,
        },
    ];
};

const buildCentralPlantChillerOptions = (airHandlingEquipment: AirHandlingEquipment) => {
    if (airHandlingEquipment.airHandlingEquipmentCoolingType === "hydronic")
        return ["air_cooled_chiller", "water_cooled_chiller"];

    return ["air_cooled_chiller", "water_cooled_chiller", "none"];
};

const buildCentralPlantHeatPumpOptions = (airHandlingEquipment: AirHandlingEquipment, centralPlant: CentralPlant) => {
    if (
        airHandlingEquipment.airHandlingEquipmentHeatingType === "hydronic" &&
        centralPlant.centralPlantHeaterType === "none"
    )
        return ["air_source_heat_pump", "ground_source_heat_pump"];
    return ["air_source_heat_pump", "ground_source_heat_pump", "none"];
};

const buildCentralPlantHeaterTerminalUnitOptions = (
    _airHandlingEquipment: AirHandlingEquipment,
    _centralPlant: CentralPlant
) => [
    "fan_coil_units",
    "baseboards",
    "constant_volume_boxes",
    "variable_air_volume_boxes",
    "water_loop_heat_pump",
    "split_air_source_heat_pump",
    "none",
];

const buildCentralPlantBoilerOptions = () => {
    const options = ["electric_resistance_boiler", "condensing_gas_boiler", "gas_boiler", "none"];
    return options;
};

export const buildTerminalUnitSurveyUIStruct = (
    airHandlingEquipment: AirHandlingEquipment,
    centralPlantHeater: CentralPlantHeater,
    centralPlantCooler: CentralPlantCooler,
    terminalCooler: TerminalCooler,
    terminalHeater: TerminalHeater,
    rooftopUnit: RooftopUnit
): SurveyUIStruct<TerminalUnits> => [
    {
        label: "How is the remaining space conditioned?",
        type: "label",
        key: null,
        image: <TerminalUnitsImage />,
        description: "Equipment that controls space temperature without using water for heat transfer.",
        show: true,
    },
    {
        label: "Which type of cooling units is used?",
        key: "terminalCoolerUnits",
        type: "select",
        options: buildCoolerTerminalUnitsOptions(airHandlingEquipment, rooftopUnit, terminalHeater),
        show: !centralPlantCooler.centralPlantCoolerExists,
    },
    {
        label: "Which type of heating units is used?",
        key: "terminalHeaterUnits",
        type: "select",
        options: buildHeaterTerminalUnitsOptions(airHandlingEquipment, rooftopUnit, terminalCooler),
        show: !centralPlantHeater.centralPlantHeaterExists,
    },
    {
        label: "Does the building have terminal cooling units?",
        key: "terminalCoolerExists",
        type: "label",
        show: false,
    },
    {
        label: "Does the building have terminal heating units?",
        key: "terminalHeaterExists",
        type: "label",
        show: false,
    },
];

const buildCoolerTerminalUnitsOptions = (
    airHandlingEquipment: AirHandlingEquipment,
    rooftopUnit: RooftopUnit,
    terminalHeater: TerminalHeater
) => {
    if (
        terminalHeater.terminalHeaterUnits === TerminalHeaterUnits.GasPtac ||
        terminalHeater.terminalHeaterUnits === TerminalHeaterUnits.ElectricResistancePtac
    ) {
        return ["cooling_ptac", NONE];
    }

    const options = ["cooling_ptac", "split_air_conditioner", "window_air_conditioner"];

    if (
        terminalHeater.terminalHeaterUnits === NONE &&
        airHandlingEquipment.airHandlingEquipmentExists &&
        !rooftopUnit.rooftopUnitExists
    ) {
        return options;
    }
    return [...options, NONE];
};

const buildHeaterTerminalUnitsOptions = (
    airHandlingEquipment: AirHandlingEquipment,
    rooftopUnit: RooftopUnit,
    terminalCooler: TerminalCooler
) => {
    if (airHandlingEquipment.airHandlingEquipmentType === "suite_air_exchangers") return [...FURNACES];
    if (terminalCooler.terminalCoolerUnits === TerminalCoolerUnits.CoolingPtac) {
        return [NONE, "gas_ptac", "electric_resistance_ptac"];
    }

    const options = [
        "condensing_gas_unit_heater",
        "electric_baseboard",
        "electric_unit_heater",
        "gas_unit_heater",
        ...FURNACES,
    ];

    if (terminalCooler.terminalCoolerUnits == null) {
        options.push(...PTAC_HEATERS);
    }

    if (
        terminalCooler.terminalCoolerUnits === NONE &&
        airHandlingEquipment.airHandlingEquipmentExists &&
        !rooftopUnit.rooftopUnitExists
    ) {
        return options;
    }
    return [...options, NONE];
};

const buildDomesticHotWaterHeaterOptions = (centralPlantHeater: CentralPlantHeater) => {
    if (
        !centralPlantHeater.centralPlantHeaterExists ||
        centralPlantHeater.centralPlantHeaterTerminalUnits === "water_loop_heat_pump"
    )
        return ["electric_heater", "gas_heater"];
    return ["electric_heater", "gas_heater", "indirect_heater"];
};

export const buildDomesticHotWaterHeaterSurveyUIStruct = (
    domesticHotWaterHeater: DomesticHotWaterHeater,
    centralPlantHeater: CentralPlantHeater
): SurveyUIStruct<DomesticHotWaterHeater> => [
    {
        label: "Does the building have domestic hot water?",
        key: "domesticHotWaterHeaterExists",
        type: "label",
        image: <DomesticHotWaterHeaterImage />,
        description: "Equipment that provides heated water to the sink, shower, tub, and other appliances.",
        show: true,
    },
    {
        label: "What type of water heater is used?",
        key: "domesticHotWaterHeaterType",
        type: "select",
        options: buildDomesticHotWaterHeaterOptions(centralPlantHeater),
        show: domesticHotWaterHeater.domesticHotWaterHeaterExists === true,
    },
    {
        label: "Is it centrally distributed?",
        key: "domesticHotWaterHeaterCentralDistribution",
        type: "boolean",
        show: domesticHotWaterHeater.domesticHotWaterHeaterExists === true,
        hint: "A non-centrally distributed domestic hot water system consists of individual domestic hot water heaters in each unit/suite.",
    },
];

export const buildOtherEquipmentSurveyUIStruct = (): SurveyUIStruct<OtherEquipment> => [
    {
        label: "Any additional equipment?",
        type: "label",
        show: true,
        key: null,
    },
    {
        label: "Select all that apply:",
        type: "subtitle",
        show: true,
        key: null,
    },
    {
        label: "Does the building have clothes dryers?",
        key: "clothesDryersExists",
        type: "checkboxWithSelect",
        options: {
            values: ["gas", "electric"],
            key: "clothesDryersType",
            selectFieldLabel: "What kind?",
        },
        show: true,
    },
    {
        label: "Does the building have clothes washers?",
        key: "clothesWashersExists",
        type: "checkbox",
        show: true,
    },
    {
        label: "Does the building have elevators?",
        key: "elevatorsExists",
        type: "checkbox",
        show: true,
    },
    {
        label: "Does the building have rooftop photovoltaics?",
        key: "rooftopPhotovoltaicsExists",
        type: "checkbox",
        show: true,
    },
    {
        label: "Does the building have escalators?",
        key: "escalatorExists",
        type: "checkbox",
        show: true,
    },
];

export interface EquipmentAndInstallationYearSurveyItem<T> {
    label: string;
    key: keyof T;
    unit?: string;
    type: string;
    placeholder?: string;
    show?: boolean;
    options?: string[];
}

export interface EquipmentAndInstallationYearSurveySection<T> {
    sectionTitle: string;
    show: boolean;
    key: keyof EquipmentSurvey | null;
    items: EquipmentAndInstallationYearSurveyItem<T>[];
}

interface TerminalUnitsAndHeatpump extends TerminalUnits, HeatPump {}

export const HEAT_PUMP_EXISTS_LABEL = "Does the building have heat pump terminal units?";

export interface EquipmentInstallationYearAndSizeStruct {
    rooftopUnit: EquipmentAndInstallationYearSurveySection<RooftopUnit>;
    airHandlingEquipment: EquipmentAndInstallationYearSurveySection<AirHandlingEquipment>;
    centralPlant: EquipmentAndInstallationYearSurveySection<CentralPlant>;
    terminalUnits: EquipmentAndInstallationYearSurveySection<TerminalUnitsAndHeatpump>;
    domesticHotWaterHeater: EquipmentAndInstallationYearSurveySection<DomesticHotWaterHeater>;
    otherEquipment: EquipmentAndInstallationYearSurveySection<OtherEquipment>;
}

/** Keys with no wizard row `key` (e.g. derived read-only); copy matches survey wording where it exists. */
export const EQUIPMENT_FIELD_AFFIX_LABELS: Record<string, string> = {
    rooftopUnitCoolingType: "Cooling type",
    heatPumpType: "Heat pump type",
    heatPumpHeatingCoefficientOfPerformance: "Heating coefficient of performance",
    heatPumpCoolingCoefficientOfPerformance: "Cooling coefficient of performance",
    heatPumpHeatingLoadRatio: "Heating load ratio",
    heatPumpCoolingLoadRatio: "Cooling load ratio",
};

const shouldShowTerminalHeaterCoolerSize = (terminalHeater: TerminalHeater, terminalCooler: TerminalCooler) =>
    !!terminalHeater.terminalHeaterExists &&
    PTAC_HEATERS.includes(terminalHeater.terminalHeaterUnits as string) &&
    (terminalCooler.terminalCoolerUnits == null ||
        terminalCooler.terminalCoolerUnits === TerminalCoolerUnits.CoolingPtac);

const shouldShowTerminalCoolerSize = (terminalHeater: TerminalHeater, terminalCooler: TerminalCooler) =>
    !!terminalCooler.terminalCoolerExists &&
    !(
        terminalCooler.terminalCoolerUnits === TerminalCoolerUnits.CoolingPtac &&
        !!terminalHeater.terminalHeaterExists &&
        PTAC_HEATERS.includes(terminalHeater.terminalHeaterUnits as string)
    );

const shouldShowTerminalCoolerInstallationYear = (terminalHeater: TerminalHeater, terminalCooler: TerminalCooler) =>
    !!terminalCooler.terminalCoolerExists && !PTAC_HEATERS.includes(terminalHeater.terminalHeaterUnits as string);

export const buildEquipmentInstallationYearAndSizeStruct = (
    roofTopUnit: RooftopUnit,
    airHandlingEquipment: AirHandlingEquipment,
    centralPlantCooler: CentralPlantCooler,
    centralPlantHeater: CentralPlantHeater,
    centralPlantHeatPump: CentralPlantHeatPump,
    terminalCooler: TerminalCooler,
    terminalHeater: TerminalHeater,
    terminalHeatPump: HeatPump,
    domesticHotWaterHeater: DomesticHotWaterHeater,
    otherEquipment: OtherEquipment
): EquipmentInstallationYearAndSizeStruct => ({
    rooftopUnit: {
        sectionTitle: "Packaged rooftop units",
        show: !!roofTopUnit.rooftopUnitExists,
        key: "rooftopUnit",
        items: [
            {
                label: "What is their total capacity?",
                key: "rooftopUnitSupplyAirRate",
                type: "number",
                unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.rooftopUnitSupplyAirRate,
                placeholder: SIZE_INPUT_PLACEHOLDER,
            },
            {
                label: "When were they installed?",
                type: "number",
                key: "rooftopUnitAverageInstallationYear",
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },
        ],
    },
    airHandlingEquipment: {
        sectionTitle: `${
            airHandlingEquipment.airHandlingEquipmentType
                ? equipmentKeyToLabelMap[airHandlingEquipment.airHandlingEquipmentType]
                : "Air handler"
        }`,
        show: !!airHandlingEquipment.airHandlingEquipmentExists,
        key: "airHandlingEquipment",
        items: [
            {
                label: "What is its total capacity?",
                key: "airHandlingEquipmentSupplyAirRate",
                type: "number",
                unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.airHandlingEquipmentSupplyAirRate,
                placeholder: SIZE_INPUT_PLACEHOLDER,
            },
            {
                label: "When was it installed?",
                type: "number",
                key: "airHandlingEquipmentAverageInstallationYear",
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },
        ],
    },
    centralPlant: {
        sectionTitle: "Central plant",
        show:
            centralPlantCooler.centralPlantCoolerExists === true ||
            centralPlantHeatPump.centralPlantHeatPumpExists === true ||
            centralPlantHeater.centralPlantHeaterExists === true,
        key: null,
        items: [
            {
                label: "What is the total capacity of the boiler?",
                key: "centralPlantHeaterSize",
                type: "number",
                unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.centralPlantHeaterSize,
                placeholder: SIZE_INPUT_PLACEHOLDER,
                show: !!centralPlantHeater.centralPlantHeaterExists,
            },
            {
                label: "When were the boilers installed?",
                type: "number",
                key: "centralPlantHeaterAverageInstallationYear",
                show: !!centralPlantHeater.centralPlantHeaterExists,
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },
            {
                label: "What is the total capacity of the heat pumps?",
                key: "centralPlantHeatPumpSize",
                type: "number",
                unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.centralPlantHeatPumpSize,
                placeholder: SIZE_INPUT_PLACEHOLDER,
                show: !!centralPlantHeatPump.centralPlantHeatPumpExists,
            },
            {
                label: "When were the heat pumps installed (avg)?",
                type: "number",
                key: "centralPlantHeatPumpAverageInstallationYear",
                show: !!centralPlantHeatPump.centralPlantHeatPumpExists,
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },
            {
                label: "What is the total capacity of the chiller?",
                key: "centralPlantCoolerSize",
                type: "number",
                unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.centralPlantCoolerSize,
                placeholder: SIZE_INPUT_PLACEHOLDER,
                show: !!centralPlantCooler.centralPlantCoolerExists,
            },
            {
                label: "When were the chillers installed (avg)?",
                type: "number",
                key: "centralPlantCoolerAverageInstallationYear",
                show: !!centralPlantCooler.centralPlantCoolerExists,
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },
        ],
    },
    terminalUnits: {
        sectionTitle: "Terminal units",
        show: !!(
            terminalCooler.terminalCoolerExists ||
            terminalHeater.terminalHeaterExists ||
            terminalHeatPump.heatPumpExists ||
            centralPlantHeater.centralPlantHeaterTerminalUnits === "water_loop_heat_pump" ||
            centralPlantHeater.centralPlantHeaterTerminalUnits === "split_air_source_heat_pump"
        ),
        key: null,
        items: [
            {
                label: "What is the total heating capacity of the terminal units?",
                key: "terminalHeaterSize",
                show: !!terminalHeater.terminalHeaterExists,
                type: "number",
                placeholder: SIZE_INPUT_PLACEHOLDER,
                unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.terminalHeaterSize,
            },
            {
                label: "What is the total cooling capacity of the terminal units?",
                key: "terminalHeaterCoolerSize",
                show: shouldShowTerminalHeaterCoolerSize(terminalHeater, terminalCooler),
                type: "number",
                placeholder: SIZE_INPUT_PLACEHOLDER,
                unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.terminalHeaterCoolerSize,
            },
            {
                label: "When were the PTAC units installed (avg)?",
                type: "number",
                show: !!terminalHeater.terminalHeaterExists,
                key: "terminalHeaterAverageInstallationYear",
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },
            {
                label: "What is the total cooling capacity of the terminal units?",
                key: "terminalCoolerSize",
                show: shouldShowTerminalCoolerSize(terminalHeater, terminalCooler),
                placeholder: SIZE_INPUT_PLACEHOLDER,
                type: "number",
                unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.terminalCoolerSize,
            },
            {
                label: "When were the cooling terminal units installed (avg)?",
                type: "number",
                show: shouldShowTerminalCoolerInstallationYear(terminalHeater, terminalCooler),
                key: "terminalCoolerAverageInstallationYear",
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },
            {
                label: "What is the total size of the heat pump terminal units?",
                key: "heatPumpSize",
                show:
                    !!terminalHeatPump.heatPumpExists ||
                    centralPlantHeater.centralPlantHeaterTerminalUnits === "water_loop_heat_pump" ||
                    centralPlantHeater.centralPlantHeaterTerminalUnits === "split_air_source_heat_pump",
                placeholder: SIZE_INPUT_PLACEHOLDER,
                type: "number",
                unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.heatPumpSize,
            },
            {
                label: "When were the heat pump terminal units installed (avg)?",
                type: "number",
                show:
                    !!terminalHeatPump.heatPumpExists ||
                    centralPlantHeater.centralPlantHeaterTerminalUnits === "water_loop_heat_pump" ||
                    centralPlantHeater.centralPlantHeaterTerminalUnits === "split_air_source_heat_pump",
                key: "heatPumpInstallationYear",
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },
        ],
    },
    domesticHotWaterHeater: {
        sectionTitle: "Domestic hot water heater",
        show: !!domesticHotWaterHeater.domesticHotWaterHeaterExists,
        key: "domesticHotWaterHeater",
        items: [
            {
                label: "What is its capacity?",
                key: "domesticHotWaterHeaterSize",
                type: "number",
                placeholder: SIZE_INPUT_PLACEHOLDER,
                unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.domesticHotWaterHeaterSize,
            },
            {
                label: "When was it installed?",
                type: "number",
                key: "domesticHotWaterHeaterAverageInstallationYear",
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },
        ],
    },
    otherEquipment: {
        sectionTitle: "Additional equipment",
        show: !!(
            otherEquipment.clothesDryersExists ||
            otherEquipment.clothesWashersExists ||
            otherEquipment.elevatorsExists ||
            otherEquipment.rooftopPhotovoltaicsExists ||
            otherEquipment.escalatorExists
        ),
        key: "otherEquipment",
        items: [
            {
                label: "What is their energy density?",
                key: "clothesDryersEnergyDensity",
                unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.clothesDryersEnergyDensity,
                type: "number",
                show: !!otherEquipment.clothesDryersExists,
                placeholder: ENERGY_DENSITY_PLACEHOLDER,
            },
            {
                label: "When were they installed (avg)?",
                type: "number",
                key: "clothesDryersAverageInstallationYear",
                show: !!otherEquipment.clothesDryersExists,
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },

            {
                label: "What is their energy density?",
                key: "clothesWashersEnergyDensity",
                unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.clothesWashersEnergyDensity,
                type: "number",
                placeholder: ENERGY_DENSITY_PLACEHOLDER,
                show: !!otherEquipment.clothesWashersExists,
            },
            {
                label: "When were they installed (avg)?",
                type: "number",
                key: "clothesWashersAverageInstallationYear",
                show: !!otherEquipment.clothesWashersExists,
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },
            {
                label: "How many?",
                key: "elevatorsQuantity",
                type: "number",
                placeholder: QUANTITY_INPUT_PLACEHOLDER,
                show: !!otherEquipment.elevatorsExists,
            },
            {
                label: "When were they installed (avg)?",
                type: "number",
                key: "elevatorsAverageInstallationYear",
                show: !!otherEquipment.elevatorsExists,
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },

            {
                label: "What is their total capacity?",
                key: "rooftopPhotovoltaicsSize",
                unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.rooftopPhotovoltaicsSize,
                type: "number",
                show: !!otherEquipment.rooftopPhotovoltaicsExists,
                placeholder: SIZE_INPUT_PLACEHOLDER,
            },
            {
                label: "When were they installed (avg)?",
                type: "number",
                key: "rooftopPhotovoltaicsAverageInstallationYear",
                show: !!otherEquipment.rooftopPhotovoltaicsExists,
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },
            {
                label: "How many?",
                key: "escalatorQuantity",
                type: "number",
                placeholder: QUANTITY_INPUT_PLACEHOLDER,
                show: !!otherEquipment.escalatorExists,
            },
            {
                label: "When were they installed (avg)?",
                type: "number",
                key: "escalatorInstallationYear",
                show: !!otherEquipment.escalatorExists,
                placeholder: YEAR_INPUT_PLACEHOLDER,
            },
        ],
    },
});

export const resetEquipmentInstallationYearAndSize = (
    setRooftopUnit: React.Dispatch<React.SetStateAction<RooftopUnit>>,
    setAirHandlingEqipment: React.Dispatch<React.SetStateAction<AirHandlingEquipment>>,
    setCentralPlantCooler: React.Dispatch<React.SetStateAction<CentralPlantCooler>>,
    setCentralPlantHeater: React.Dispatch<React.SetStateAction<CentralPlantHeater>>,
    setCentralPlantHeatPump: React.Dispatch<React.SetStateAction<CentralPlantHeatPump>>,
    setCentralPlantGenericEquipment: unknown,
    setTerminalHeater: React.Dispatch<React.SetStateAction<TerminalHeater>>,
    setTerminalCooler: React.Dispatch<React.SetStateAction<TerminalCooler>>,
    setDomesticHotWaterHeater: React.Dispatch<React.SetStateAction<DomesticHotWaterHeater>>,
    setOtherEquipment: React.Dispatch<React.SetStateAction<OtherEquipment>>
) => {
    setRooftopUnit((prev) => ({
        ...prev,
        rooftopUnitAverageInstallationYear: null,
        rooftopUnitSupplyAirRate: null,
    }));

    setAirHandlingEqipment((prev) => ({
        ...prev,
        airHandlingEquipmentAverageInstallationYear: null,
        airHandlingEquipmentSupplyAirRate: null,
    }));

    setCentralPlantCooler((prev) => ({
        ...prev,
        centralPlantCoolerSize: null,
        centralPlantCoolerAverageInstallationYear: null,
    }));

    setCentralPlantHeater((prev) => ({
        ...prev,
        centralPlantHeaterAverageInstallationYear: null,
        centralPlantHeaterSize: null,
    }));

    setCentralPlantHeatPump((prev: CentralPlantHeatPump) => ({
        ...prev,
        centralPlantHeatPumpAverageInstallationYear: null,
        centralPlantHeatPumpSize: null,
    }));

    setTerminalHeater((prev) => ({
        ...prev,
        terminalHeaterAverageInstallationYear: null,
        terminalHeaterSize: null,
    }));

    setTerminalCooler((prev) => ({
        ...prev,
        terminalCoolerAverageInstallationYear: null,
        terminalCoolerSize: null,
    }));

    setDomesticHotWaterHeater((prev) => ({
        ...prev,
        domesticHotWaterHeaterSize: null,
        domesticHotWaterHeaterAverageInstallationYear: null,
    }));

    setOtherEquipment((prev) => ({
        ...prev,
        clothesDryersAverageInstallationYear: null,
        clothesDryersEnergyDensity: null,
        clothesWashersAverageInstallationYear: null,
        clothesWashersEnergyDensity: null,
        elevatorsAverageInstallationYear: null,
        elevatorsQuantity: null,
        rooftopPhotovoltaicsAverageInstallationYear: null,
        rooftopPhotovoltaicsSize: null,
        escalatorInstallationYear: null,
        escalatorQuantity: null,
    }));
};
export function getValidatedEquipmentState<T>(obj: T, struct: SurveyUIStruct<T>) {
    const newObj = { ...obj };

    struct.forEach((item) => {
        if (item.key === null) return;

        const itemIsNotVisibleAnymore = item.key !== null && !item.show;

        const val = newObj[item.key];
        const itemIsNotSelectable = item.type === "select" && !(item as SurveyUIStructSelect<T>).options.includes(val);

        if (itemIsNotVisibleAnymore || itemIsNotSelectable) newObj[item.key] = null as any;
    });
    return newObj;
}

export const surveyValueExists = (val: string | null) => val !== null && val !== NONE;

const syncExistsWithSelection = <T extends object>(slice: T, existsKey: keyof T, selectionKey: keyof T): T =>
    ({
        ...slice,
        [existsKey]: surveyValueExists(slice[selectionKey] as string | null),
    }) as T;

const isCentralPlantHeatPumpTerminalUnits = (terminalUnits: CentralPlantHeater["centralPlantHeaterTerminalUnits"]) =>
    terminalUnits === "water_loop_heat_pump" || terminalUnits === "split_air_source_heat_pump";

export const syncEquipmentExistsFlagsFromSelections = (survey: EquipmentSurvey): EquipmentSurvey => ({
    ...survey,
    rooftopUnit: {
        ...survey.rooftopUnit,
        rooftopUnitCoolingType: survey.rooftopUnit.rooftopUnitExists ? "direct_expansion" : "none",
    },
    centralPlantHeater: syncExistsWithSelection(
        survey.centralPlantHeater,
        "centralPlantHeaterExists",
        "centralPlantHeaterType"
    ),
    centralPlantCooler: syncExistsWithSelection(
        survey.centralPlantCooler,
        "centralPlantCoolerExists",
        "centralPlantCoolerType"
    ),
    centralPlantHeatPump: {
        ...survey.centralPlantHeatPump,
        centralPlantHeatPumpExists:
            surveyValueExists(survey.centralPlantHeatPump.centralPlantHeatPumpType) ||
            isCentralPlantHeatPumpTerminalUnits(survey.centralPlantHeater.centralPlantHeaterTerminalUnits),
    },
    centralPlantGenericEquipment: syncExistsWithSelection(
        survey.centralPlantGenericEquipment,
        "centralPlantGenericEquipmentExists",
        "centralPlantGenericEquipmentName"
    ),
    terminalCooler: syncExistsWithSelection(survey.terminalCooler, "terminalCoolerExists", "terminalCoolerUnits"),
    terminalHeater: syncExistsWithSelection(survey.terminalHeater, "terminalHeaterExists", "terminalHeaterUnits"),
    heatPump: syncExistsWithSelection(survey.heatPump, "heatPumpExists", "heatPumpType"),
});

const mergeSurveySlice = <T extends object>(defaults: T, api: object | null | undefined): T =>
    ({ ...defaults, ...(api ?? {}) }) as T;

const mergeOptionalSurveySlice = <T extends object>(defaults: T, apiSlice: unknown): T =>
    mergeSurveySlice(defaults, (apiSlice as object | null | undefined) ?? null);

const buildEquipmentSurveyBase = (api: EquipmentSurvey): EquipmentSurvey => ({
    rooftopUnit: mergeOptionalSurveySlice(DEFAULT_ROOFTOP_UNIT, api.rooftopUnit),
    airHandlingEquipment: mergeOptionalSurveySlice(DEFAULT_AIR_HANDLING_EQUIPMENT, api.airHandlingEquipment),
    centralPlantCooler: mergeOptionalSurveySlice(DEFAULT_CENTRAL_PLANT_COOLER, api.centralPlantCooler),
    centralPlantHeater: mergeOptionalSurveySlice(DEFAULT_CENTRAL_PLANT_HEATER, api.centralPlantHeater),
    centralPlantHeatPump: mergeOptionalSurveySlice(DEFAULT_CENTRAL_PLANT_HEAT_PUMP, api.centralPlantHeatPump),
    centralPlantGenericEquipment: centralPlantGenericEquipmentFromGenericHvacRow(api.genericHVACEquipment?.[0]),
    genericHVACEquipment: api.genericHVACEquipment ?? [],
    terminalCooler: mergeOptionalSurveySlice(DEFAULT_TERMINAL_COOLER, api.terminalCooler),
    terminalHeater: mergeOptionalSurveySlice(DEFAULT_TERMINAL_HEATER, api.terminalHeater),
    heatPump: mergeOptionalSurveySlice(DEFAULT_HEAT_PUMP, api.heatPump),
    domesticHotWaterHeater: mergeOptionalSurveySlice(DEFAULT_DOMESTIC_HOT_WATER, api.domesticHotWaterHeater),
    otherEquipment: mergeOptionalSurveySlice(DEFAULT_OTHER_EQUIPMENT, api.otherEquipment),
});

const centralPlantGenericEquipmentFromGenericHvacRow = (
    row: GenericHvacEquipment | undefined
): CentralPlantGenericEquipment => {
    if (!row) return { ...DEFAULT_CENTRAL_PLANT_GENERIC_EQUIPMENT };
    return {
        centralPlantGenericEquipmentExists: surveyValueExists(row.name ?? null),
        centralPlantGenericEquipmentName: row.name ?? null,
        centralPlantGenericEquipmentSize: row.size ?? null,
        centralPlantGenericEquipmentSizeUnits: row.sizeUnits ?? null,
        centralPlantGenericEquipmentCoefficientOfPerformance: row.coefficientOfPerformance ?? null,
        centralPlantGenericEquipmentFuel: row.fuel ?? null,
        centralPlantGenericEquipmentEndUse: row.endUse ?? null,
        centralPlantGenericEquipmentLoadRatio: row.loadRatio ?? null,
        centralPlantGenericEquipmentInstallationYear: row.installationYear ?? null,
    };
};

export const equipmentSurveyApiResponseToFormState = (
    api: EquipmentSurvey | null | undefined
): EquipmentSurvey | null => {
    if (!api) return null;
    return syncEquipmentExistsFlagsFromSelections(buildEquipmentSurveyBase(api));
};

export const updatePageStackOnNextClick = (
    activeView: ActiveView,
    setPageStack: React.Dispatch<React.SetStateAction<ActiveView[]>>
) => {
    setPageStack((prev) => [...prev, activeView]);
};

export const updatePageStackAndViewOnBackClick = (
    setActiveView: (view: ActiveView) => void,
    pageStack: ActiveView[],
    setPageStack: React.Dispatch<React.SetStateAction<ActiveView[]>>
) => {
    if (pageStack.length < 1) return;

    const previousPage = pageStack[pageStack.length - 1];
    setActiveView(previousPage);
    setPageStack((prev) => {
        const copy = [...prev];
        copy.pop();
        return copy;
    });
};

export const useSaveButtonIsDisabled = (
    rooftopUnit: RooftopUnit,
    airHandlingEquipment: AirHandlingEquipment,
    centralPlantCooler: CentralPlantCooler,
    centralPlantHeater: CentralPlantHeater,
    terminalCooler: TerminalCooler,
    terminalHeater: TerminalHeater,
    terminalHeatPump: HeatPump,
    domesticHotWaterHeater: DomesticHotWaterHeater,
    otherEquipment: OtherEquipment,
    interfaceStructure: EquipmentInstallationYearAndSizeStruct,
    booleanSurveyResult: boolean | null
) =>
    useMemo(() => {
        if (booleanSurveyResult === false) return false;
        if (booleanSurveyResult === null) return true;

        let atLeastOneValueIsEmpty = false;

        if (interfaceStructure.rooftopUnit.show) {
            interfaceStructure.rooftopUnit.items.forEach(({ key, show }) => {
                if (show !== false && rooftopUnit[key] === null) atLeastOneValueIsEmpty = true;
            });
        }

        if (interfaceStructure.airHandlingEquipment.show) {
            interfaceStructure.airHandlingEquipment.items.forEach(({ key, show }) => {
                if (show !== false && airHandlingEquipment[key] === null) atLeastOneValueIsEmpty = true;
            });
        }

        if (interfaceStructure.centralPlant.show) {
            interfaceStructure.centralPlant.items.forEach(({ key, show }) => {
                if (key in centralPlantCooler) {
                    if (show !== false && centralPlantCooler[key as keyof CentralPlantCooler] === null)
                        atLeastOneValueIsEmpty = true;
                    else if (show !== false && centralPlantHeater[key as keyof CentralPlantHeater] === null)
                        atLeastOneValueIsEmpty = true;
                }
            });
        }

        if (interfaceStructure.terminalUnits.show) {
            interfaceStructure.terminalUnits.items.forEach(({ key, show }) => {
                if (show !== false) {
                    if (key in terminalCooler) {
                        if (terminalCooler[key as keyof TerminalCooler] === null) atLeastOneValueIsEmpty = true;
                    } else if (key in terminalHeater) {
                        if (terminalHeater[key as keyof TerminalHeater] === null) atLeastOneValueIsEmpty = true;
                    } else if (key in terminalHeatPump) {
                        if (terminalHeatPump[key as keyof HeatPump] === null) atLeastOneValueIsEmpty = true;
                    }
                }
            });
        }

        if (interfaceStructure.domesticHotWaterHeater.show) {
            interfaceStructure.domesticHotWaterHeater.items.forEach(({ key, show }) => {
                if (show !== false && domesticHotWaterHeater[key] === null) atLeastOneValueIsEmpty = true;
            });
        }

        if (interfaceStructure.otherEquipment.show) {
            interfaceStructure.otherEquipment.items.forEach(({ key, show }) => {
                if (show !== false && otherEquipment[key] === null) atLeastOneValueIsEmpty = true;
            });
        }
        return atLeastOneValueIsEmpty;
    }, [
        rooftopUnit,
        airHandlingEquipment,
        centralPlantCooler,
        centralPlantHeater,
        terminalCooler,
        terminalHeater,
        terminalHeatPump,
        domesticHotWaterHeater,
        otherEquipment,
        booleanSurveyResult,
        interfaceStructure,
    ]);
